import './TourAverages.scss';
import '../Admin.scss';
import '../../../styles/_table.scss';

import AdminEditWarning from 'components/Admin/AdminEditWarning/AdminEditWarning';
import { Button } from '@trackman/web-shared-components';
import TourAveragesTable from 'components/Admin/TourAverages/TourAveragesTable/TourAveragesTable';
import TourAveragesTableToolbar from 'components/Admin/TourAverages/TourAveragesTableToolbar/TourAveragesTableToolbar';
import { useMemo, useState } from 'react';
import useSetPageTitle from 'utilities/useSetPageTitle';
import { useParameterCategories } from 'queryHooks/TrackmanParameters/useParameterCategories';
import { isTrackmanParametersV2Enabled } from 'utilities/enabledFeaturesHelper';
import { TourAverageParameterOption, TourAveragesData, TourAveragesVersion } from 'types/admin/tourAverages';
import { mapCategoriesToParameterOptions } from './tourAveragesParameterOptions';
import { useSaveTourAverages } from 'queryHooks/Admin/TourAverages/useSaveTourAverages';
import { useTourAverages } from 'queryHooks/Admin/TourAverages/useTourAverages';
import { useTourAveragesEditor } from './useTourAveragesEditor';

type TourAveragesEditorProps = {
  initialData: TourAveragesData;
  parameterOptions: TourAverageParameterOption[];
  isSaving: boolean;
  onSave: (data: TourAveragesData) => void;
};

const TourAveragesEditor = ({ initialData, parameterOptions, isSaving, onSave }: TourAveragesEditorProps) => {
  const {
    tourAveragesData,
    newTourParameterData,
    availableParameters,
    handleDeleteColumn,
    handleYearChange,
    handleNewParameterSelected,
    handleNewParameterUnitChanged,
    handleAddParameter,
    handleScoreChange,
  } = useTourAveragesEditor({ initialData, parameterOptions });

  return (
    <>
      <TourAveragesTableToolbar
        availableParameters={availableParameters}
        year={tourAveragesData.year}
        newTourParameterData={newTourParameterData}
        onYearChange={handleYearChange}
        onNewParameterSelected={handleNewParameterSelected}
        onNewParameterUnitChanged={handleNewParameterUnitChanged}
        onAddParameter={handleAddParameter}
      />
      <TourAveragesTable
        kind='PGA'
        tourAveragesScoresArray={tourAveragesData.pga}
        handleDeleteColumn={handleDeleteColumn}
        onScoreChange={(columnIndex, club, value) => handleScoreChange('PGA', columnIndex, club, value)}
      />
      <TourAveragesTable
        kind='LPGA'
        tourAveragesScoresArray={tourAveragesData.lpga}
        handleDeleteColumn={handleDeleteColumn}
        onScoreChange={(columnIndex, club, value) => handleScoreChange('LPGA', columnIndex, club, value)}
      />
      <Button
        className='styled-button primary save-pga-tables'
        intent='primary'
        isLoading={isSaving}
        onClick={() => onSave(tourAveragesData)}
      >
        Save tables
      </Button>
    </>
  );
};

export const TourAverages = () => {
  useSetPageTitle({ title: 'Tour Averages tables' });
  const [version, setVersion] = useState(TourAveragesVersion.V1);
  const { data: tourAveragesData } = useTourAverages(version);
  const saveTourAverages = useSaveTourAverages(version);
  const { data: categories = [] } = useParameterCategories(version === TourAveragesVersion.V2);

  const parameterOptions = useMemo(() => mapCategoriesToParameterOptions(categories), [categories]);
  const editorKey = tourAveragesData ? `${version}-${tourAveragesData.id ?? ''}` : undefined;

  return (
    <>
      <div className='default-page-header'>
        <h1 className='page-title'>Tour Averages tables</h1>
      </div>
      <div className='admin-content-container'>
        <AdminEditWarning />
        {isTrackmanParametersV2Enabled && (
          <div className='tour-averages-version-toggle'>
            <Button
              className='styled-button'
              intent={version === TourAveragesVersion.V1 ? 'primary' : 'secondary'}
              onClick={() => setVersion(TourAveragesVersion.V1)}
            >
              V1
            </Button>
            <Button
              className='styled-button'
              intent={version === TourAveragesVersion.V2 ? 'primary' : 'secondary'}
              onClick={() => setVersion(TourAveragesVersion.V2)}
            >
              V2
            </Button>
          </div>
        )}
        {tourAveragesData && (
          <TourAveragesEditor
            key={editorKey}
            initialData={tourAveragesData}
            parameterOptions={parameterOptions}
            isSaving={saveTourAverages.isPending}
            onSave={saveTourAverages.mutate}
          />
        )}
      </div>
    </>
  );
};

export default TourAverages;
